import { isEqual } from 'lodash';
import { useCallback, useEffect } from 'react';

import { useRecordNotFoundHandler } from '@/hooks/useRecordNotFoundHandler';
import { AugmentedUpdateTeamInput } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { useTeamModalQuery } from './graphql/TeamModal.generated';
import { TeamFormShape } from './TeamModal.types';

export const mapFormDataToUpdateInput = (
  initial: Readonly<TeamFormShape> | undefined,
  current: TeamFormShape,
  teamId: string
): AugmentedUpdateTeamInput => {
  const input: AugmentedUpdateTeamInput = { id: teamId, update: {} };

  if (!initial) return input;

  if (current.name !== initial.name) {
    input.update.name = current.name;
  }

  // Calculate household assignment changes
  const initialHouseholds = current._initialAssignedHouseholdIds;
  const currentHouseholds = current.assignedHouseholdIds;

  if (!isEqual(currentHouseholds, initialHouseholds)) {
    const householdsToAdd = currentHouseholds.filter(
      (id) => !initialHouseholds.includes(id)
    );
    const householdsToRemove = initialHouseholds.filter(
      (id) => !currentHouseholds.includes(id)
    );

    if (householdsToAdd.length > 0) {
      input.update.addAssignedHouseholdIDs = householdsToAdd;
    }

    if (householdsToRemove.length > 0) {
      input.update.removeAssignedHouseholdIDs = householdsToRemove;
    }
  }

  // Calculate member assignment changes
  const initialMembers = current._initialEmployeeIds;
  const currentMembers = current.employeeIds;

  if (!isEqual(currentMembers, initialMembers)) {
    const membersToAdd = currentMembers.filter(
      (id) => !initialMembers.includes(id)
    );
    const membersToRemove = initialMembers.filter(
      (id) => !currentMembers.includes(id)
    );

    if (membersToAdd.length > 0) {
      input.update.addEmployeeIDs = membersToAdd;
    }

    if (membersToRemove.length > 0) {
      input.update.removeEmployeeIDs = membersToRemove;
    }
  }

  return input;
};

export const useSyncTeamDataToForm = (
  reset: (values: TeamFormShape) => void,
  teamId: string,
  onNotFound: () => void
) => {
  const { recoverMissingRecord } = useRecordNotFoundHandler({
    message: 'This team is no longer available.',
    onNotFound,
  });
  const { data, loading } = useTeamModalQuery({
    variables: { teamId },
    skip: !teamId,
  });

  const team = getNodes(data?.teams)[0];

  const syncDataToForm = useCallback(() => {
    if (team) {
      const assignedHouseholdIds =
        getNodes(team.assignedHouseholds)?.map((h) => h.id) ?? [];
      const memberIds = getNodes(team.employees)?.map((e) => e.id) ?? [];
      const formData: TeamFormShape = {
        name: team.name,
        assignedHouseholdIds,
        _initialAssignedHouseholdIds: assignedHouseholdIds,
        employeeIds: memberIds,
        _initialEmployeeIds: memberIds,
      };
      reset(formData);
    }
  }, [reset, team]);

  // Sync data to form whenever team data changes
  useEffect(() => {
    if (team) {
      syncDataToForm();
    } else if (data && teamId) {
      recoverMissingRecord();
    }
  }, [data, recoverMissingRecord, syncDataToForm, team, teamId]);

  return { team, syncDataToForm, loading };
};
